/**
 * Shift-click range selection over a displayed list of issues.
 *
 * Extracted from the issues list (WHA-3456) so the protocol picker uses the
 * same gesture rather than growing a second, subtly different copy. The
 * documents domain has two more implementations of this idea; they REPLACE the
 * selection instead of extending it, so they are deliberately not unified here.
 */

export interface RangeSelectInput {
  /** Currently ticked ids. Never mutated. */
  selected: ReadonlySet<number>;
  /** Every selectable id in DISPLAY order — the index space a range spans. */
  orderedIds: readonly number[];
  clickedId: number;
  /** The last id toggled without Shift, or null. */
  anchorId: number | null;
  shiftKey: boolean;
  /**
   * How a Shift range combines with what is already ticked.
   *
   * `"union"` — add the range, never remove. The issues list's behaviour: it
   *   starts with nothing selected, so union is all it ever needs.
   * `"anchor"` — set the whole range to the ANCHOR's current state, so a range
   *   can be UNticked. The protocol picker starts with everything ticked,
   *   where union would make Shift-click a no-op on the common gesture.
   */
  mode?: "union" | "anchor";
}

export interface RangeSelectResult {
  selected: Set<number>;
  /** Always the clicked id — a plain click and a Shift-click both re-anchor. */
  anchorId: number;
}

/**
 * The next selection for one click on a row's tick box.
 *
 * Pure: clearing a stray text selection during a Shift-drag is a DOM effect and
 * stays at the call site.
 */
function shiftRange(
  input: RangeSelectInput & { anchorId: number },
  mode: NonNullable<RangeSelectInput["mode"]>
): Set<number> | null {
  const { selected, orderedIds, clickedId, anchorId } = input;
  const from = orderedIds.indexOf(anchorId);
  const to = orderedIds.indexOf(clickedId);
  // The -1 bail-out is load-bearing: it is what happens when the anchor row
  // has been filtered out from under the user. Falling through to a plain
  // toggle is better than ranging over a index that no longer means anything.
  if (from === -1 || to === -1) return null;
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  const next = new Set(selected);
  // In anchor mode the anchor decides: dragging out of a ticked run unticks
  // it, dragging out of an unticked run ticks it.
  const include = mode === "union" || selected.has(anchorId);
  for (const id of orderedIds.slice(lo, hi + 1)) {
    if (include) next.add(id);
    else next.delete(id);
  }
  return next;
}

export function applyPMRangeSelect(input: RangeSelectInput): RangeSelectResult {
  const { selected, clickedId, anchorId, shiftKey, mode = "union" } = input;

  if (shiftKey && anchorId != null) {
    const ranged = shiftRange({ ...input, anchorId }, mode);
    if (ranged) return { selected: ranged, anchorId: clickedId };
  }

  const next = new Set(selected);
  if (next.has(clickedId)) next.delete(clickedId);
  else next.add(clickedId);
  return { selected: next, anchorId: clickedId };
}
